import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import FormQuestionMapping from '@/views/config_views/AutoVerificationManagment/FormQuestionMapping/FormQuestionMapping.vue';
import {
  CREATE_SERVICE_MAPPINGS,
  GET_LINKED_ONBOARDING_FORMS_API,
  GET_MAPPING_OPTIONS,
  GET_QUESTION_MAPPINGS,
  GET_VERIFICATION_FORMS,
  GET_VERIFICATION_SERVICES,
  get_request_config,
} from '@/utils/system_constants/constant.js';

export default {
  name: 'ConfigureVerificationForm',
  mixins: [alert_mixin],
  components: { FormQuestionMapping },
  data: () => ({
    loading: false,
    saving: false,
    verification_form_id: null,
    verification_form: null,
    services: [],
    service_state: {},
    active_service_id: '',
    active_tab: 'step1',
    linked_onboarding_forms: [],
    loading_linked_onboarding_forms: false,
    selected_onboarding_form_id: '',
  }),
  computed: {
    verification_form_display_name() {
      return String(this.verification_form?.form_name || this.verification_form?.name || '').trim() || 'Verification form';
    },
    verification_survey_form_id() {
      return this.get_survey_form_id(this.verification_form);
    },
    selected_onboarding_link() {
      const selected = this.normalize_id(this.selected_onboarding_form_id);
      if (!selected) return null;
      return (this.linked_onboarding_forms || []).find((link) => this.get_onboarding_form_id(link) === selected) || null;
    },
    selected_onboarding_form_name() {
      const link = this.selected_onboarding_link;
      return String(link?.onboarding_form_name || link?.onboarding_form?.form_name || link?.onboarding_form?.name || '').trim();
    },
    active_service() {
      const id = this.normalize_id(this.active_service_id);
      if (!id) return null;
      return (this.services || []).find((service) => String(service?.id) === String(id)) || null;
    },
    active_service_state() {
      const service_id = this.active_service?.id;
      if (!service_id) return null;
      return this.service_state?.[String(service_id)] || null;
    },
    active_service_has_changes() {
      const service_id = this.active_service?.id;
      if (!service_id) return false;

      const state = this.service_state?.[String(service_id)] || {};
      const current = state.field_mappings || {};
      const initial = state.initial_field_mappings || {};

      return (
        JSON.stringify(this.normalize_mappings_object(current)) !== JSON.stringify(this.normalize_mappings_object(initial))
      );
    },
  },
  mounted() {
    const id = this.$route?.query?.verification_form_id ? Number(this.$route.query.verification_form_id) : null;
    this.verification_form_id = id;
    this.reload_all();
  },
  methods: {
    merge_service_required_fields(service) {
      const service_required_fields = Array.isArray(service?.required_fields) ? service.required_fields : [];
      const system_verification_fields = Array.isArray(service?.system_verification_fields)
        ? service.system_verification_fields
        : [];
      const merged = [...service_required_fields, ...system_verification_fields];
      const seen = new Set();

      return merged.filter((field) => {
        const key = String(field?.key || field?.name || field || '').trim();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    resolve_field_type(service, required_field) {
      const system_verification_fields = Array.isArray(service?.system_verification_fields)
        ? service.system_verification_fields
        : [];
      const normalized_required_field = String(required_field || '').trim();
      const is_system_field = system_verification_fields.some(
        (field) => String(field?.key || field?.name || field || '').trim() === normalized_required_field,
      );
      return is_system_field ? 'system' : 'api';
    },

    normalize_id(value) {
      return String(value ?? '').trim();
    },

    get_verification_form_id(form) {
      return form?.verification_form_id ?? form?.form_id ?? form?.id ?? null;
    },

    get_survey_form_id(form) {
      return form?.survey_form_id ?? form?.form_id ?? null;
    },

    get_onboarding_form_id(link) {
      return this.normalize_id(link?.onboarding_form_id || link?.onboarding_form || link?.onboarding_form?.id);
    },

    get_form_link_id(link) {
      return this.normalize_id(link?.form_link_id || link?.link_id || link?.id);
    },

    normalize_mappings_object(obj) {
      return Object.keys(obj || {})
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .reduce((acc, key) => {
          Reflect.set(acc, key, String(Reflect.get(obj, key) ?? ''));
          return acc;
        }, {});
    },

    set_active_service(service_id) {
      this.active_service_id = this.normalize_id(service_id);
    },

    get_service_progress(service) {
      const service_id = service?.id;
      if (!service_id) return { mapped: 0, total: 0, missing: [] };

      const state = this.service_state?.[String(service_id)] || {};
      const required_fields = Array.isArray(state.required_fields) ? state.required_fields : [];
      const field_mappings = state.field_mappings || {};

      const missing = required_fields
        .map((field) => String(field?.key || field?.name || field || '').trim())
        .filter(Boolean)
        .filter((field_key) => !Reflect.get(field_mappings, field_key));

      const result = {
        mapped: Math.max(0, required_fields.length - missing.length),
        total: required_fields.length,
        missing,
      };
      return result;
    },

    get_next_incomplete_service_id() {
      const incomplete = (this.services || []).find((service) => {
        const { total, missing } = this.get_service_progress(service);
        return total > 0 && missing.length > 0;
      });
      return this.normalize_id(incomplete?.id);
    },

    go_to_next_incomplete_service() {
      const next = this.get_next_incomplete_service_id();
      if (next) this.set_active_service(next);
      else this.show_success('All services look ready.');
    },

    format_required_field_label(field_key) {
      const text = String(field_key || '')
        .replace(/[_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!text) return '';
      return text.toLowerCase();
    },

    init_service_state(service_id) {
      const key = String(service_id);
      if (Reflect.get(this.service_state ?? {}, key)) return;
      this.service_state = {
        ...(this.service_state || {}),
        [key]: {
          loading: false,
          required_fields: [],
          questions: [],
          field_mappings: {},
          initial_field_mappings: {},
          mapping_options_message: '',
        },
      };
    },

    async reload_all() {
      if (!this.verification_form_id) {
        this.show_error('Missing verification form id.');
        return;
      }

      this.loading = true;
      try {
        await Promise.all([this.load_verification_form(), this.load_services()]);
        await this.load_linked_onboarding_forms();

        await Promise.all(
          (this.services || [])
            .filter((service) => service?.id)
            .map((service) => this.load_service_mapping_state(service.id)),
        );

        if (!this.normalize_id(this.active_service_id) && (this.services || []).length) {
          this.active_service_id = this.normalize_id(this.services[0]?.id);
        }
      } finally {
        this.loading = false;
      }
    },

    async load_verification_form() {
      const response = await axios.get(GET_VERIFICATION_FORMS, get_request_config());
      const data = response?.data ?? {};

      if (data?.status && data.status !== 'success') {
        throw new Error(data?.message || 'Failed to load verification forms.');
      }

      const items = Array.isArray(data?.data) ? data.data : [];
      this.verification_form = items.find((item) => String(this.get_verification_form_id(item)) === String(this.verification_form_id)) || null;
    },

    async load_services() {
      const response = await axios.get(GET_VERIFICATION_SERVICES, get_request_config());
      const data = response?.data ?? {};

      if (data?.status && data.status !== 'success') {
        throw new Error(data?.message || 'Failed to load verification services.');
      }

      const items = Array.isArray(data?.data) ? data.data : [];
      this.services = items.map((service) => ({
        ...service,
        required_fields: this.merge_service_required_fields(service),
      }));
    },

    async load_linked_onboarding_forms() {
      if (!this.verification_form_id) return;
      this.loading_linked_onboarding_forms = true;

      await axios.get(
        GET_LINKED_ONBOARDING_FORMS_API,
        get_request_config({ params: { verification_form_id: this.verification_form_id } }),
      )
        .then((response) => {
          const links = response?.data?.data ?? [];
          this.linked_onboarding_forms = links;
          if (!this.normalize_id(this.selected_onboarding_form_id) && links.length) {
            this.selected_onboarding_form_id = this.get_onboarding_form_id(links[0]) || '';
          }
        })
        .catch((error) => {
          this.linked_onboarding_forms = [];
          this.show_error(error?.response?.data?.message || error?.message || 'Failed to load linked onboarding forms.');
        })
        .finally(() => {
          this.loading_linked_onboarding_forms = false;
        });
    },

    async load_service_mapping_state(service_id) {
      if (!service_id || !this.verification_form_id) return;

      const service_key = String(service_id);
      this.init_service_state(service_key);

      const current = Reflect.get(this.service_state, service_key) || {};
      if (current.loading) return;

      this.service_state = {
        ...(this.service_state || {}),
        [service_key]: { ...current, loading: true, mapping_options_message: '' },
      };

      try {
        const service_from_list = (this.services || []).find((item) => String(item?.id) === String(service_id)) || null;

        const mapping_response = await axios.get(
          GET_MAPPING_OPTIONS,
          get_request_config({
            params: {
              service_id: Number(service_id),
              verification_form_id: Number(this.verification_form_id),
            },
          }),
        );
        const mapping_data = mapping_response?.data ?? {};

        const required_fields = this.merge_service_required_fields(mapping_data?.service || service_from_list);

        const questions = (mapping_data?.questions ?? [])
          .map(({ id, question_name, section }) => ({
            id,
            label: question_name ?? 'Untitled question',
            section: section ?? '',
          }))
          .filter(({ id }) => id != null);

        const question_mappings_response = await axios.get(
          GET_QUESTION_MAPPINGS,
          get_request_config({
            params: {
              service_id: Number(service_id),
              verification_form_id: Number(this.verification_form_id),
            },
          }),
        );
        const question_mapping_data = question_mappings_response?.data ?? {};
        const mappings = Array.isArray(question_mapping_data?.mappings) ? question_mapping_data.mappings : [];

        const allowed_question_ids = new Set(questions.map((question_item) => String(question_item?.id)).filter(Boolean));
        const field_mappings = {};
        mappings.forEach((mapping_item) => {
          const required_field = mapping_item?.required_field;
          const question_id = mapping_item?.question_id;
          if (!required_field) return;
          if (question_id == null) return;
          if (allowed_question_ids.size && !allowed_question_ids.has(String(question_id))) return;
          field_mappings[String(required_field)] = String(question_id);
        });

        const mapping_options_message =
          required_fields?.length && !questions.length
            ? 'No questions found for this verification form yet. Create questions first, then map required fields.'
            : '';

        this.service_state = {
          ...(this.service_state || {}),
          [service_key]: {
            ...((Reflect.get(this.service_state ?? {}, service_key)) || {}), 
            loading: false,
            service: service_from_list || null,
            required_fields,
            questions,
            field_mappings,
            initial_field_mappings: { ...field_mappings },
            mapping_options_message,
          },
        };
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to load service mapping options.';
        this.service_state = {
          ...(this.service_state || {}),
          [service_key]: {
            ...(Reflect.get(this.service_state ?? {}, service_key) || {}),
            loading: false,
            required_fields: [],
            questions: [],
            field_mappings: {},
            initial_field_mappings: {},
            mapping_options_message: message,
          },
        };
      }
    },

    on_field_mapping_change(service_id, field, evt) {
      const key = String(field?.key || field?.name || field || '').trim();
      const value = evt?.target?.value || '';
      const service_key = String(service_id);
      this.init_service_state(service_key);

      const state = Reflect.get(this.service_state ?? {}, service_key) || {};
      const nextMappings = { ...(state.field_mappings || {}) };
      if (value) Reflect.set(nextMappings, key, value);
      else Reflect.deleteProperty(nextMappings, key);

      this.service_state = {
        ...(this.service_state || {}),
        [service_key]: {
          ...state,
          field_mappings: nextMappings,
        },
      };
    },

    discard_active_service_changes() {
      const service_id = this.active_service?.id;
      if (!service_id) return;
      const key = String(service_id);
      const state = Reflect.get(this.service_state ?? {}, key) || {};
      this.service_state = {
        ...(this.service_state || {}),
        [key]: {
          ...state,
          field_mappings: { ...(state.initial_field_mappings || {}) },
        },
      };
    },

    async save_active_service() {
      if (!this.verification_form_id) {
        this.show_error('Missing verification form id.');
        return;
      }

      const service_id = this.active_service?.id;
      if (!service_id) {
        this.show_error('Select a service first.');
        return;
      }

      const key = String(service_id);
      const state = Reflect.get(this.service_state ?? {}, key) || {};
      const required_fields = Array.isArray(state.required_fields) ? state.required_fields : [];
      const field_mappings = state.field_mappings || {};
      const service = state.service || this.active_service;

      const mappings = required_fields
        .map((field) => {
          const required_field = String(field?.key || field?.name || field || '').trim();
          const question_id_raw = Reflect.get(field_mappings, required_field);
          const question_id = question_id_raw !== '' && question_id_raw != null ? Number(question_id_raw) : null;
          return {
            service_id: Number(service_id),
            verification_form_id: Number(this.verification_form_id),
            required_field,
            question_id,
            field_type: this.resolve_field_type(service, required_field),
          };
        })
        .filter((mapping_item) => mapping_item.required_field);

      const missing = mappings
        .filter((mapping_item) => !mapping_item.question_id || Number.isNaN(mapping_item.question_id))
        .map((mapping_item) => mapping_item.required_field);

      if (missing.length) {
        this.show_error(`Please select a question for: ${missing.join(', ')}`);
        return;
      }

      const loader = await this.show_loader('Saving mapping...');
      try {
        await axios.post(CREATE_SERVICE_MAPPINGS, { mappings }, get_request_config());
        this.service_state = {
          ...(this.service_state || {}),
          [key]: {
            ...state,
            initial_field_mappings: { ...(state.field_mappings || {}) },
          },
        };
        this.show_success('Mapping saved.');
      } catch (error) {
        this.show_error(error?.response?.data?.message || error?.message || 'Failed to save mapping.');
      } finally {
        await this.close_loader(loader);
      }
    },

    async save_all() {
      if (!this.verification_form_id) {
        this.show_error('Missing verification form id.');
        return;
      }

      const service_ids = (this.services || []).map((service) => String(service?.id || '')).filter(Boolean);

      const invalid = [];
      service_ids.forEach((service_id) => {
        const state = this.service_state?.[String(service_id)] || {};
        const required_fields = Array.isArray(state.required_fields) ? state.required_fields : [];
        const field_mappings = state.field_mappings || {};
        const missing = required_fields
          .map((field) => String(field?.key || field?.name || field || '').trim())
          .filter(Boolean)
          .filter((field_key) => !Reflect.get(field_mappings, field_key));
        if (missing.length) {
          const service = (this.services || []).find((item) => String(item?.id) === String(service_id));
          invalid.push({ service_name: service?.name || service?.title || `Service ${service_id}`, missing });
        }
      });

      if (invalid.length) {
        const first = invalid[0];
        const list = first.missing.slice(0, 6).join(', ');
        const more = first.missing.length > 6 ? ` (+${first.missing.length - 6} more)` : '';
        this.show_error(`${first.service_name} is missing mappings for: ${list}${more}`);
        return;
      }

      this.saving = true;
      const loader = await this.show_loader('Saving configuration...');
      try {
        await Promise.all(
          service_ids.map((service_id) => {
            const state = this.service_state?.[String(service_id)] || {};
            const required_fields = Array.isArray(state.required_fields) ? state.required_fields : [];
            const field_mappings = state.field_mappings || {};
            const service = state.service || (this.services || []).find((item) => String(item?.id) === String(service_id));

            const mappings = required_fields
              .map((field) => {
                const required_field = String(field?.key || field?.name || field || '').trim();
                const question_id_raw = Reflect.get(field_mappings, required_field);
                const question_id = question_id_raw !== '' && question_id_raw != null ? Number(question_id_raw) : null;
                return {
                  service_id: Number(service_id),
                  verification_form_id: Number(this.verification_form_id),
                  required_field,
                  question_id,
                  field_type: this.resolve_field_type(service, required_field),
                };
              })
              .filter((mapping_item) => mapping_item.required_field);

            return axios.post(CREATE_SERVICE_MAPPINGS, { mappings }, get_request_config());
          }),
        );

        this.show_success('Configuration saved successfully.');
      } catch (error) {
        this.show_error(error?.response?.data?.message || error?.message || 'Failed to save configuration.');
      } finally {
        await this.close_loader(loader);
        this.saving = false;
      }
    },

    go_to_question_builder() {
      if (!this.verification_survey_form_id) {
        this.show_error('Verification form question builder is not available (missing survey_form_id).');
        return;
      }

      this.$router.push({
        name: 'CreateSections',
        query: {
          form_id: String(this.verification_survey_form_id),
          form_name: this.verification_form_display_name,
          verification_form_id: String(this.verification_form_id || ''),
        },
      });
    },

    go_back() {
      if (this.$router) this.$router.back();
    },
  },
};
